import { Fragment, useEffect } from 'react';

import { getVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import type { WCoupleContextView } from '@zola/svc-web-api-ts-client';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks';

import { CamelCasedPropertiesDeep } from 'type-fest';

import { receivedBulkInquiryToStorefront } from '~/actions/types/couplesInquiriesActionTypes';
import ClientApp from '~/ClientApp';
import ClientRouter from '~/ClientRouter';
import { useUserContext } from '~/contexts/UserContext';
import { VendorSearchNavigationProvider } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationProvider';
import { useAppDispatch } from '~/reducers';
import ApiService from '~/util/apiService';

import SearchResultsPageModalsAndToasts from '../../SearchResultsPageModalsAndToasts';
import { ByStyleCategoryCarouselForSRP } from '../ByStyleCategoryCarousel/ByStyleCategoryCarouselForSrp';
import { ExpertAdviceForSRP } from '../ExpertAdvice/ExpertAdviceForSrp';
import { PageSection } from '../PageSection/PageSection';
import { AdjacentVendors } from './components/AdjacentVendors/AdjacentVendors';
import { MobileSearchFilters } from './components/PreAuth/MobileSearchFilters';
import { PageHeading } from './components/PreAuth/PageHeading';
import { PreAuthSearchResults } from './components/PreAuth/PreAuthSearchResults';
import { ZolaTopPicks } from './components/PreAuth/ZolaTopPicks';
import { RealWeddings } from './components/RealWeddings/RealWeddings';
import {
  SearchBreadcrumb,
  useSearchBreadcrumbs,
} from './components/SearchBreadcrumb/SearchBreadcrumb';
import { SRPSearchBarProvider } from './components/SearchControls/SRPSearchBarContext';
import { SearchResultsHead } from './components/SearchResultsHead/SearchResultsHead';
import { SeoContentBlock } from './components/SeoContentBlock/SeoContentBlock';
import { SeoCrossLinks } from './components/SeoCrossLinks/SeoCrossLinks';
import { useSearchResultsPageContext } from './contexts/SearchResultsPageContext';
import { VendorGridCardsProvider } from './contexts/VendorGridCardsProvider';

import styles from './preAuthSearchResultsPage.module.less';

const MobileWebBreadcrumb = () => {
  const breadcrumbList = useSearchBreadcrumbs();

  if (breadcrumbList.length < 2) {
    return <Fragment />;
  }

  const item = breadcrumbList[breadcrumbList.length - 2];

  return (
    <nav
      style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}
      className={styles.onlyMobile}
    >
      <ul>
        <li>
          <LinkV2 className={styles.backLink} arrow arrowPosition="prefix" href={item.to}>
            {item.label}
          </LinkV2>
        </li>
      </ul>
    </nav>
  );
};

const Breadcrumb = () => {
  return (
    <PageSection noPadding stretchToEdge>
      <MobileWebBreadcrumb />
      <div className={styles.desktopBreadcrumb}>
        <SearchBreadcrumb />
      </div>
    </PageSection>
  );
};

const ScrollTop = () => {
  const { canonicalUrl } = useSearchResultsPageContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [canonicalUrl]);

  return <Fragment />;
};

const ZolaTopPicksWrapper: React.FC = ({ children }) => {
  return <div className={styles.zolaTopPicksWrapper}>{children}</div>;
};

/**
 * Pre-auth winner of an SRP A/B test variation:
 * - Grid of cards to list of cards
 * - Card presentation is wider, includes a review and a vendor description
 * - Location of favorite button is moved
 * - On desktop, a left rail of filter controls is used
 * - H1 header is changed
 *
 * Control was vs a grid of cards
 */
export const PreAuthSearchResultsPage = () => {
  const userContext = useUserContext();
  const { vendorSearch } = useSearchResultsPageContext();
  const dispatch = useAppDispatch();

  useForceUpdateOnWindowResize();

  // If the page becomes a post-auth page, fetch inquiries
  useEffect(() => {
    if (userContext && !userContext.is_guest) {
      const params = {
        inquiryRequest: {
          vendor_type: vendorSearch.selectedTaxonomy?.key
            ? getVendorType(vendorSearch.selectedTaxonomy?.key)
            : undefined,
          limit: 500,
        },
      };

      ApiService.post<CamelCasedPropertiesDeep<WCoupleContextView>>(
        `/web-marketplace-api/v1/marketplace-context/for-couple`,
        params
      )
        .then((response) => {
          const { storefrontUuidToInquiryUuidMap } = response;

          if (storefrontUuidToInquiryUuidMap) {
            dispatch(receivedBulkInquiryToStorefront(storefrontUuidToInquiryUuidMap));
          }
        })
        .catch(() => null);
    }
  }, [dispatch, userContext, vendorSearch.selectedTaxonomy?.key]);

  return (
    <div className={styles.pageWrapper}>
      <ScrollTop />
      <VendorSearchNavigationProvider>
        <SearchResultsHead isPreAuthSrp />
        <Breadcrumb />
        <SRPSearchBarProvider>
          <PageHeading />
        </SRPSearchBarProvider>
        <div className={styles.divider}>
          <hr />
        </div>

        <MobileSearchFilters />
        <ZolaTopPicks tabWrapper={ZolaTopPicksWrapper}>
          <div className={styles.divider}>
            <hr />
          </div>
        </ZolaTopPicks>

        <VendorGridCardsProvider>
          <PreAuthSearchResults />
        </VendorGridCardsProvider>
        <ByStyleCategoryCarouselForSRP className={styles.withTopBorder} />
        <AdjacentVendors className={styles.withTopBorder} />
        <ExpertAdviceForSRP className={styles.withTopBorder} />
        <RealWeddings className={styles.withTopBorder} />
        <SeoContentBlock className={styles.withTopBorder} />
        <SeoCrossLinks className={styles.withTopBorder} />
      </VendorSearchNavigationProvider>

      <ClientApp useStore={false}>
        <ClientRouter>
          <SearchResultsPageModalsAndToasts />
        </ClientRouter>
      </ClientApp>
    </div>
  );
};

export default PreAuthSearchResultsPage;
